// FocalSparkline.tsx — a small inline-SVG sparkline of a lens profile's focal
// estimate across scans (Devices page, ARCHITECTURE.md §14). One series in
// the brand stroke, a 2px line with an end marker, hollow markers for
// estimates that were not fused (outlier-gated pending, rejected, a
// re-refine recorded as a duplicate, or taken while the profile was locked), and a native <title> tooltip per point.
// The min/max read-out sits beside it in text ink, never the series color.
import type { DeviceEstimate } from '../api';

export interface FocalSparklineProps {
  points: DeviceEstimate[];
  width: number; // profile width in px: converts fx/W to focal px
}

const FUSED = new Set(['initial', 'fused', 'rebased', 'replaced']);

const W = 140;
const H = 36;
const PAD = 5;

export function FocalSparkline({ points, width }: FocalSparklineProps) {
  const pts = points.filter((p) => Number.isFinite(p.intrinsics?.fx) && p.intrinsics.fx > 0);
  if (pts.length < 2) return null;
  const f = pts.map((p) => p.intrinsics.fx * (width || 1));
  const lo = Math.min(...f), hi = Math.max(...f);
  const span = hi - lo || Math.max(hi * 1e-3, 1e-6);
  const x = (i: number): number => PAD + (i * (W - 2 * PAD)) / (pts.length - 1);
  const y = (v: number): number => H - PAD - ((v - lo) / span) * (H - 2 * PAD);
  const path = f.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const unit = width ? 'px' : '×W';
  const digits = width ? 0 : 4;
  return (
    <div className="d-flex align-items-center gap-2 mt-2">
      <svg
        className="focal-spark"
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Focal estimate across ${pts.length} scans, ${lo.toFixed(digits)} to ${hi.toFixed(digits)} ${unit}`}
      >
        <path d={path} fill="none" stroke="var(--bs-primary)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {f.map((v, i) => {
          const p = pts[i];
          const last = i === f.length - 1;
          const rejected = !FUSED.has(p.outcome);
          return (
            <g key={`${p.scan_id}-${i}`}>
              <circle
                cx={x(i)}
                cy={y(v)}
                r={last || rejected ? 4 : 2}
                fill={rejected ? 'var(--bs-body-bg)' : 'var(--bs-primary)'}
                stroke={rejected ? 'var(--bs-secondary-color)' : 'var(--bs-body-bg)'}
                strokeWidth={rejected ? 1.5 : 1}
              />
              {/* Hit target larger than the mark, carrying the tooltip. */}
              <circle cx={x(i)} cy={y(v)} r={8} fill="transparent">
                <title>
                  {`scan ${p.scan_id}: f ${v.toFixed(digits)} ${unit} · ${p.outcome}${p.rms_px ? ` · RMS ${p.rms_px.toFixed(2)} px` : ''}`}
                </title>
              </circle>
            </g>
          );
        })}
      </svg>
      <div className="small text-body-secondary lh-sm">
        <div>max {hi.toFixed(digits)}</div>
        <div>min {lo.toFixed(digits)} {unit}</div>
      </div>
    </div>
  );
}
